// rng.ts — seeded, deterministic PRNG. NO Math.random anywhere in src/.
//
// We need a generator that is (a) deterministic across runs, (b) allocation-free
// in the per-tick hot path, and (c) fast. SplitMix64 is the reference algorithm,
// but a true 64-bit implementation in JS requires BigInt, and every BigInt op
// allocates — which would violate the "no allocation in the hot path" rule and
// add GC pressure (a determinism risk per EFFICIENCY.md §B.3).
//
// So we run SplitMix in 32-bit integer space using Math.imul-based mixing
// (the well-known "splitmix32" finalizer). State is a single Uint32; every step
// is pure integer math on a number — zero allocation, fully reproducible. The
// public surface (nextU32 / float01 / int) matches what the engine needs.

const GOLDEN_GAMMA = 0x9e3779b9; // 2^32 / golden ratio, the SplitMix increment.

export class Rng {
  /** Internal 32-bit state, kept as an unsigned int via `>>> 0`. */
  private state: number;

  constructor(seed: number) {
    // Normalise the seed to a u32; a 0 seed is fine for this finalizer.
    this.state = seed >>> 0;
  }

  /** Re-seed in place (no allocation). Useful for deterministic test resets. */
  reseed(seed: number): void {
    this.state = seed >>> 0;
  }

  /** Next 32-bit unsigned integer in [0, 2^32). Allocation-free. */
  nextU32(): number {
    // splitmix32: advance state, then finalize with two xor-shift-multiplies.
    this.state = (this.state + GOLDEN_GAMMA) >>> 0;
    let z = this.state;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
    return (z ^ (z >>> 15)) >>> 0;
  }

  /** Float in [0, 1) with 32 bits of precision. Allocation-free. */
  float01(): number {
    // Divide by 2^32 to land in [0,1).
    return this.nextU32() / 4294967296;
  }

  /**
   * Uniform integer in [0, n). Returns 0 for n <= 1 (defensive; callers should
   * pass n >= 1). Uses the multiply-shift mapping (one draw, no rejection loop):
   * floor(u32 * n / 2^32) is uniform over [0,n). Slight bias for non-power-of-2
   * n is acceptable for game logic and keeps it allocation-free.
   */
  int(n: number): number {
    if (n <= 1) return 0;
    return Math.floor((this.nextU32() * n) / 4294967296);
  }
}
